import { useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import {
  FieldPath,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { BackendRoute } from '../../../../config/index';
import { useLocale } from '../../../../hooks/index';
import { ApiService } from '../../../../services/index';
import {
  fileWithStorageDisplayName,
  getFileDisplayName,
} from '../../../../utils/file-upload-duplicate-names.util';
import { getTranslation } from '../../../../utils/get-translation.util';
import {
  FormSchema,
  MAX_EVENT_UPLOAD_FILES,
  MAX_FILE_SIZE,
  subFilesSchema,
} from '../event-form.validation';
import { useEventFileUploadDuplicates } from './use-event-file-upload-duplicates';

export type EventDocumentsFile = {
  abort?: () => void;
  doc?: { name?: string; size?: number; type?: string } | File;
  docLink?: string;
  id?: string;
  loadProgress?: number;
};

/**
 * Every form field that holds one bucket of event documents: the event-level "General" bucket of
 * each event type, and the per-stakeholder bucket on each issuance row.
 */
export type EventDocumentsFieldPath =
  | 'companyIncorporation.files'
  | 'stepTwo.files'
  | `companyIncorporation.issuances.${number}.files`
  | `optionalStep.share-issuance.issuanceItems.${number}.files`;

/**
 * Mirrors the copy under the dropzone and the schema's ACCEPTED_FILE_MIME_TYPES. Note the pairing:
 * `application/vnd.ms-excel` is `.xls` and the OpenXML type is `.xlsx` — the two were transposed in
 * the uploader this replaces, so `.xls` files were offered under the wrong MIME type.
 */
const ACCEPTED_UPLOAD_TYPES = {
  'application/msword': ['.doc'],
  'application/pdf': ['.pdf'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'text/csv': ['.csv'],
};

export const ACCEPTED_UPLOAD_EXTENSIONS = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpeg,.jpg';

export type UseEventDocumentsBucketParams = {
  clearErrors: UseFormClearErrors<FormSchema>;
  field: EventDocumentsFieldPath;
  formData: UseFormGetValues<FormSchema>;
  /**
   * Fields kept identical to `field` on every write. A stakeholder issued on several rows of the
   * same event shows one bucket (see `buildStakeholderDocumentBuckets`); mirroring is what makes
   * that bucket's files reach every one of their rows instead of only the first.
   */
  mirrorFields?: EventDocumentsFieldPath[];
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

/**
 * One document bucket's worth of upload behaviour — drop, progress, abort, delete and duplicate
 * detection — extracted so the General and per-stakeholder buckets share a single implementation
 * instead of the copy-per-screen the three previous uploaders had.
 */
export const useEventDocumentsBucket = ({
  clearErrors,
  field,
  formData,
  mirrorFields,
  setError,
  setFormData,
}: UseEventDocumentsBucketParams) => {
  const { messagesLocale } = useLocale();

  // The field paths above are a union, which react-hook-form's generics cannot narrow to a single
  // value type. Both casts are confined to these two helpers; every caller stays typed.
  const readFiles = useCallback(
    (): EventDocumentsFile[] =>
      (formData(field as FieldPath<FormSchema>) as EventDocumentsFile[] | undefined) ?? [],
    [field, formData],
  );

  const writeFiles = useCallback(
    (files: EventDocumentsFile[], options?: { shouldValidate?: boolean }) => {
      setFormData(field as FieldPath<FormSchema>, files as never, options);
      // Every write, not just the settled one: aborts and deletions must clear the mirrored rows
      // the same way an upload fills them.
      mirrorFields?.forEach((mirrorField) =>
        setFormData(mirrorField as FieldPath<FormSchema>, files as never, options),
      );
    },
    [field, mirrorFields, setFormData],
  );

  const files = readFiles();
  const uploadedCount = files.reduce((count, file) => (file.docLink ? count + 1 : count), 0);
  const isReachedUploadMaximum = files.length >= MAX_EVENT_UPLOAD_FILES;
  const isUploading = files.some((file) => !file.docLink);

  const {
    hasUnresolvedDuplicateNames,
    isFileNameDuplicate,
    removeFileToRename,
    renameModal,
    renameModalFolderNames,
    resolveDisplayName,
    validateDropFiles,
  } = useEventFileUploadDuplicates({
    files,
    onRenameInForm: ({ currentName, newName }) => {
      writeFiles(
        readFiles().map((file) =>
          getFileDisplayName(file.doc) === currentName
            ? {
                ...file,
                doc:
                  file.doc instanceof File
                    ? new File([file.doc], newName, { type: file.doc.type })
                    : { ...file.doc, name: newName },
              }
            : file,
        ),
        { shouldValidate: true },
      );
    },
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        fileRejections.forEach(({ file, errors }) => {
          errors.forEach(({ code }) => {
            if (code === 'file-too-large') {
              setError(field, { message: getTranslation('FILE_SIZE_TOO_LARGE'), type: 'manual' });
              return;
            }
            if (code === 'file-invalid-type') {
              setError(field, {
                message: messagesLocale['INVALID_FILE_TYPE'].replace('{fileType}', file.name),
                type: 'manual',
              });
            }
          });
        });
        return;
      }

      if (isReachedUploadMaximum) return;

      const existingFiles = readFiles();
      if (validateDropFiles(acceptedFiles, existingFiles)) {
        setError(field, { message: messagesLocale['FILE_EXISTS'], type: 'manual' });
        return;
      }

      const filesToUpload = acceptedFiles.slice(0, MAX_EVENT_UPLOAD_FILES - existingFiles.length);

      filesToUpload.forEach(async (file) => {
        const { abort, callback } = ApiService.uploadFile<string>();
        const id = uuidv4();
        const pending = { abort, doc: file, docLink: '', id, loadProgress: 1 };

        const { error, success } = subFilesSchema.safeParse([pending]);
        if (!success) {
          setError(field, error.issues[0]);
          return;
        }
        clearErrors(field);

        writeFiles([...readFiles(), pending], { shouldValidate: true });

        const link = await callback(
          { endpoint: BackendRoute.SHARE_CLASSES, routePath: ['file'] },
          ['file', file],
          {
            onAboard: () => writeFiles(readFiles().filter((entry) => entry.id !== id)),
            onUploadProgress: (progress) =>
              writeFiles(
                readFiles().map((entry) =>
                  entry.id === id ? { ...entry, loadProgress: progress } : entry,
                ),
              ),
          },
        );

        if (!link) return;

        writeFiles(
          readFiles().map((entry) =>
            entry.id === id
              ? {
                  ...entry,
                  doc: fileWithStorageDisplayName(file, link),
                  docLink: link,
                  loadProgress: 100,
                }
              : entry,
          ),
          { shouldValidate: true },
        );
      });
    },
    [
      clearErrors,
      field,
      isReachedUploadMaximum,
      messagesLocale,
      readFiles,
      setError,
      validateDropFiles,
      writeFiles,
    ],
  );

  // No `onDropRejected`: react-dropzone calls it *after* `onDrop`, which already reports each
  // rejection with the reason that applies to it. A generic "format not supported" there overwrote
  // the specific one, so an oversized PDF was reported as the wrong kind of problem.
  const { getInputProps, getRootProps } = useDropzone({
    accept: ACCEPTED_UPLOAD_TYPES,
    disabled: isReachedUploadMaximum,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
    onDrop,
  });

  const handleDeleteFile = useCallback(
    (id: string) => {
      const current = readFiles().find((file) => file.id === id);
      removeFileToRename(getFileDisplayName(current?.doc));
      // A file still in flight has to have its request aborted, or the upload completes and
      // re-adds a row the user just removed.
      if (current && !current.docLink) current.abort?.();

      const remaining = readFiles().filter((file) => file.id !== id);
      writeFiles(remaining, { shouldValidate: true });
      if (remaining.length === 0) clearErrors(field);
    },
    [clearErrors, field, readFiles, removeFileToRename, writeFiles],
  );

  return {
    files,
    getInputProps,
    getRootProps,
    handleDeleteFile,
    hasUnresolvedDuplicateNames,
    isFileNameDuplicate,
    isReachedUploadMaximum,
    isUploading,
    removeFileToRename,
    renameModal,
    renameModalFolderNames,
    resolveDisplayName,
    uploadedCount,
  };
};
